/* ============================================================================
   ClassPerformanceChart / index.js
   Desempenho da turma ao longo do ano, com DUAS visões alternáveis:

     • "Evolução"  → eixo X = períodos (1º, 2º, 3º… + Total);
                     uma LINHA por matéria (trajetória ao longo do ano).
     • "Comparar"  → eixo X = matérias; BARRAS agrupadas por período
                     (1º vs 2º vs 3º… + Total lado a lado).

   Uso na página de informações da turma (antes dos ButtonContainer):
     import ClassPerformanceChart from '../../components/ClassPerformanceChart'
     <ClassPerformanceChart
       idClass={id_class}
       year={yearclss?.year}
       assessmentFormat={assessmentFormat}   // 'grade' | 'concept'
       assessmentRegime={assessmentRegime}   // 'BIMESTRAL' | 'TRIMESTRAL' (opcional)
     />

   Fonte de dados: getClassGradesAndConcepts(idClass, year)
     → { data: { students: [{ numerical:[{matter,periods,total}],
                              concepts:[{matter,periods,finalConcept}] }] } }

   Valor plotado (sempre a MÉDIA da turma):
     - NOTA    → por período: média das notas do período; Total: média da soma anual.
     - CONCEITO→ por período: média do conceito do período (D=1…A=4);
                 Total: média do finalConcept. Eixo/tooltip rotulam de volta em letra.
   Componente AUTOSSUFICIENTE (busca os próprios dados; compacto e colapsável).
   ========================================================================== */

   import React, { useState, useEffect, useMemo, useCallback } from 'react'
   import {
       ComposedChart, LineChart, Bar, Line, XAxis, YAxis, CartesianGrid,
       Tooltip, ResponsiveContainer,
   } from 'recharts'
   import { FiTrendingUp, FiTrendingDown, FiMinus, FiBarChart2, FiChevronDown } from 'react-icons/fi'
   import { getClassGradesAndConcepts } from '../../Api'
   import {
       Wrapper, Header, HeaderLeft, Title, Subtitle, Toggle,
       Body, ViewSwitch, SwitchGroup, SwitchButton, Legend, LegendItem, Swatch,
       ChartArea, ChartScroll, ChartInner, StateMsg, TrendBadge, TrendHint, /*LegendRow,*/ MatterTrend, LegendNote,
   } from './styles'
   
   /* ---------- paleta estável por matéria ------------------------------------- */
   // cor escolhida pelo índice alfabético da matéria → estável entre renders.
   const PALETTE = [
       '#003e4f', '#e07a5f', '#1d8b5a', 'var(--amber, #d97706)', '#5b6bd6', 'var(--red, #dc2626)',
       '#0a9396', '#9b5de5', '#2a9d8f', '#d6336c', '#7d8597', '#457b9d',
   ]
   // tons por período para a visão "Comparar" (do mais claro ao Total escuro)
   //const PERIOD_SHADES = ['#7fb3c4', '#3d8ba3', '#0a5a73', '#003e4f']
   const TOTAL_COLOR = 'var(--amber, #d97706)'
   const STUDENTS_COLOR = '#1d8b5a'   // média dos alunos (verde teal)
   
   /* conceito ↔ escala numérica */
   const CONCEPT_TO_NUM = { A: 4, B: 3, C: 2, D: 1 }
   const CONCEPT_MAX = 4   // teto da escala de conceito (A) — usado como "Total"
   const NUM_TO_CONCEPT = { 4: 'A', 3: 'B', 2: 'C', 1: 'D', 0: '—' }
   const conceptFromNum = (n) => {
       if (n == null || isNaN(n)) return '—'
       return NUM_TO_CONCEPT[Math.round(n)] || '—'
   }
   
   const norm = (s = '') => String(s).trim().toUpperCase()
   const round1 = (n) => Math.round(n * 10) / 10
   
   // metadados visuais de cada status de tendência
   const TREND_META = {
       up:     { label: 'Evoluindo', color: '#1d8b5a', Icon: FiTrendingUp },
       down:   { label: 'Caindo',    color: 'var(--red, #dc2626)', Icon: FiTrendingDown },
       stable: { label: 'Estável',   color: 'var(--amber, #d97706)', Icon: FiMinus },
   }
   
   /* ---------- componente ------------------------------------------------------ */
   const ClassPerformanceChart = ({
       idClass,
       year,
       assessmentFormat = 'grade',
       assessmentRegime,
   }) => {
       const isConcept = assessmentFormat === 'concept'
       const isTrimestral = norm(assessmentRegime) === 'TRIMESTRAL'
   
       const [view, setView] = useState('compare')  // 'compare' (barras) | 'evolution' (linhas)
       const [open, setOpen] = useState(true)
       const [loading, setLoading] = useState(false)
       const [error, setError] = useState(false)
       const [students, setStudents] = useState([])
       const [periodTotals, setPeriodTotals] = useState([])  // total do bimestre por matéria (regime nota)
   
       const load = useCallback(async () => {
           if (!idClass) return
           setLoading(true); setError(false)
           try {
               const res = await getClassGradesAndConcepts(idClass, year)
               setStudents(res?.data?.data?.students || [])
               setPeriodTotals(res?.data?.data?.periodTotals || [])
           } catch (e) {
               setError(true); setStudents([]); setPeriodTotals([])
           } finally {
               setLoading(false)
           }
       }, [idClass, year])
   
       useEffect(() => { load() }, [load])
   
       // ── agrega tudo em uma passada ──────────────────────────────────────────
       // Para cada matéria acumula, por período, soma+contagem (p/ média da turma),
       // e também a soma anual por aluno (p/ o "Total" no regime nota).
       const agg = useMemo(() => {
           const periodTotalsProp = periodTotals
           const periodLabel = isTrimestral ? 'Trim' : 'Bim'
   
           const map = new Map()
           const periodsSeen = new Set()
   
           const ensure = (name) => {
               const key = norm(name)
               if (!map.has(key)) {
                   map.set(key, { name, perPeriod: new Map(), annual: { sum: 0, count: 0 } })
               }
               return map.get(key)
           }
           const bump = (bag, idx, value) => {
               if (!bag.has(idx)) bag.set(idx, { sum: 0, count: 0 })
               const it = bag.get(idx); it.sum += value; it.count += 1
           }
   
           for (const stu of (students || [])) {
               const list = isConcept ? (stu.concepts || []) : (stu.numerical || [])
               for (const row of list) {
                   const name = (row.matter || '').trim()
                   if (!name) continue
                   const it = ensure(name)
   
                   const periods = row.periods || {}
                   for (const [k, raw] of Object.entries(periods)) {
                       const idx = parseInt(k, 10)
                       if (isNaN(idx)) continue
                       let v = null
                       if (isConcept) v = CONCEPT_TO_NUM[norm(raw)] ?? null
                       else { const n = Number(raw); v = isNaN(n) ? null : n }
                       if (v == null) continue
                       periodsSeen.add(idx)
                       bump(it.perPeriod, idx, v)
                   }
   
                   let annual = null
                   if (isConcept) {
                       annual = CONCEPT_TO_NUM[norm(row.finalConcept)] ?? null
                   } else if (typeof row.total === 'number' && !isNaN(row.total)) {
                       annual = row.total
                   } else {
                       const vals = Object.values(periods).map(Number).filter(v => !isNaN(v))
                       annual = vals.length ? vals.reduce((a, b) => a + b, 0) : null
                   }
                   if (annual != null && !isNaN(annual)) {
                       it.annual.sum += annual; it.annual.count += 1
                   }
               }
           }
   
           const periodIdxs = [...periodsSeen].sort((a, b) => a - b)
   
           // rótulo curto para o eixo X. Cortar cegamente os primeiros caracteres
           // faz nomes com prefixo igual colidirem ("EDUCAÇÃO FÍSICA" e "EDUCAÇÃO
           // ALIMENTAR" viravam ambos "EDUCAÇÃO…"). Aqui abreviamos preservando a
           // palavra distintiva (a última costuma diferenciar) e, como salvaguarda,
           // desambiguamos qualquer colisão remanescente.
           const STOP = new Set(['DE', 'DA', 'DO', 'E', 'DOS', 'DAS'])
           const shorten = (name) => {
               const raw = String(name || '').trim()
               if (raw.length <= 12) return raw
               const words = raw.split(/\s+/)
               if (words.length === 1) return raw.slice(0, 11) + '…'
               // primeira palavra abreviada + últimas palavras significativas
               const first = words[0]
               const rest = words.slice(1).filter(w => !STOP.has(w.toUpperCase()))
               const tail = rest.join(' ')
               const firstAbbr = first.length > 4 ? first.slice(0, 3) + '.' : first
               let out = `${firstAbbr} ${tail}`.trim()
               if (out.length > 16) out = out.slice(0, 15) + '…'
               return out
           }
   
           const mattersRaw = [...map.values()]
               .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
   
           // pré-calcula shorts e resolve colisões (acrescenta a 2ª palavra ou nº)
           const shortCounts = new Map()
           const shortSeen = new Map()
           const shorts = mattersRaw.map(m => shorten(m.name))
           for (const s of shorts) shortCounts.set(s, (shortCounts.get(s) || 0) + 1)
   
           const matters = mattersRaw.map((m, i) => {
               let short = shorts[i]
               if (shortCounts.get(short) > 1) {
                   // colisão: tenta usar mais palavras do nome original
                   const words = String(m.name).trim().split(/\s+/)
                   const alt = words.map(w => (w.length > 4 && !STOP.has(w.toUpperCase()) ? w.slice(0, 4) : w)).join(' ')
                   short = alt.length > 18 ? alt.slice(0, 17) + '…' : alt
                   // se ainda colidir, sufixa com contador
                   const n = (shortSeen.get(short) || 0) + 1
                   shortSeen.set(short, n)
                   if (n > 1) short = `${short} (${n})`
               }
               return {
                   ...m,
                   color: PALETTE[i % PALETTE.length],
                   short,
               }
           })
   
           const avgOf = (bag, idx) => {
               const it = bag.get(idx)
               return it && it.count ? round1(it.sum / it.count) : null
           }
           const avgAnnual = (m) => (m.annual.count ? round1(m.annual.sum / m.annual.count) : null)
   
           // média geral do CONCEITO dos alunos por matéria: junta todos os períodos
           // lançados (soma total ÷ contagem total dos conceitos convertidos em número).
           const avgConceptAll = (m) => {
               let sum = 0, count = 0
               for (const { sum: s, count: c } of m.perPeriod.values()) { sum += s; count += c }
               return count ? round1(sum / count) : null
           }
   
           // ── tendência (evoluindo / estável / caindo) ────────────────────────
           // compara o PRIMEIRO período com o ÚLTIMO período lançado da matéria.
           // NOTA: compara o APROVEITAMENTO (% da nota sobre os pontos já distribuídos no
           //   período; sem essa informação, sobre o total do período). Assim um período
           //   em andamento (poucas atividades lançadas) ou com total diferente
           //   (ex.: 25 × 30 pontos) não aparece como "caindo" sem motivo.
           // margem de "estável": conceito → 0,25 na escala 1–4; nota → 5 pontos percentuais.
           const denomPorMateria = new Map()
           for (const pt of (periodTotalsProp || [])) {
               const key = norm(pt?.matter)
               if (!key) continue
               const d = {}
               const idxs = new Set([...Object.keys(pt.totals || {}), ...Object.keys(pt.distributed || {})])
               for (const k of idxs) {
                   const dist = Number(pt.distributed?.[k])
                   const tot = Number(pt.totals?.[k])
                   d[k] = dist > 0 ? dist : (tot > 0 ? tot : null)
               }
               denomPorMateria.set(key, d)
           }
           const valorDaTendencia = (m, idx) => {
               const v = avgOf(m.perPeriod, idx)
               if (v == null || isConcept) return v
               const den = denomPorMateria.get(norm(m.name))?.[idx]
               return den ? round1((v / den) * 100) : null
           }
           const trendOf = (m) => {
               const vals = periodIdxs
                   .map((idx) => ({ idx, v: valorDaTendencia(m, idx) }))
                   .filter((p) => p.v != null)
               if (vals.length < 2) return { status: 'stable', delta: 0, insufficient: true }
               const first = vals[0].v
               const last = vals[vals.length - 1].v
               const delta = round1(last - first)
               const margin = isConcept ? 0.25 : 5
               let status = 'stable'
               if (delta > margin) status = 'up'
               else if (delta < -margin) status = 'down'
               return { status, delta, insufficient: false }
           }
   
           // aplica a tendência em cada matéria
           for (const m of matters) m.trend = trendOf(m)
   
           // tendência GERAL da turma: maioria simples entre as matérias
           // (empate ou sem dados suficientes → estável)
           const counts = { up: 0, down: 0, stable: 0 }
           let considered = 0
           for (const m of matters) {
               if (m.trend.insufficient) continue
               counts[m.trend.status]++
               considered++
           }
           let overallStatus = 'stable'
           if (considered > 0) {
               if (counts.up > counts.down && counts.up >= counts.stable) overallStatus = 'up'
               else if (counts.down > counts.up && counts.down >= counts.stable) overallStatus = 'down'
           }
           const overallTrend = { status: overallStatus, counts, considered }
   
           const periodCols = periodIdxs.map((idx) => ({
               key: `p${idx}`, idx, label: `${idx}º ${periodLabel}`,
           }))
           const hasTotal = matters.some(m => avgAnnual(m) != null)
   
           // ── dados p/ "Comparar" ─────────────────────────────────────────────
           // Estrutura IGUAL nos dois regimes: por matéria, duas séries lado a lado
           //   • "Média dos alunos" = média do resultado dos alunos na matéria
           //   • "Total"            = referência de máximo
           // CONCEITO: média = conceito médio dos alunos (todos os períodos);
           //           total = sempre A (teto da escala).
           // NOTA:     média = média da soma das notas dos alunos (acum. até o último
           //           período); total = soma dos totalGrade dos períodos lançados.
   
           // totais do bimestre por matéria (vindo do backend): id_matter → {idx: totalGrade}
           const totalsByMatter = new Map()
           for (const pt of (periodTotalsProp || [])) {
               const key = norm(pt?.matter)
               if (!key) continue
               totalsByMatter.set(key, pt.totals || {})
           }
   
           const compareConcept = matters.map((m) => ({
               matter: m.name,
               short: m.short,
               color: m.color,
               studentsAvg: avgConceptAll(m),   // conceito médio dos alunos
               total: CONCEPT_MAX,              // sempre A
           }))
   
           const compareNota = matters.map((m) => {
               const key = norm(m.name)
               // média acumulada dos alunos = média das somas dos alunos nos períodos
               // lançados. m.annual já é a média da soma de cada aluno (todos os
               // períodos presentes), então reaproveita.
               const studentsAvg = avgAnnual(m)
   
               // total acumulado = soma dos totalGrade dos períodos lançados
               const totals = totalsByMatter.get(key) || {}
               let totalAcc = 0, anyTotal = false
               for (const idx of periodIdxs) {
                   const t = Number(totals[idx])
                   if (!isNaN(t)) { totalAcc += t; anyTotal = true }
               }
               return {
                   matter: m.name,
                   short: m.short,
                   color: m.color,
                   studentsAvg: studentsAvg,
                   total: anyTotal ? round1(totalAcc) : null,
               }
           })
   
           const compareData = isConcept ? compareConcept : compareNota
           const hasTotals = compareData.some(r => r.total != null)
   
           // rótulo do período final coberto pelo acumulado (ex.: "até o 2º Bim")
           const lastPeriodIdx = periodIdxs.length ? periodIdxs[periodIdxs.length - 1] : null
           const accLabel = lastPeriodIdx
               ? `até o ${lastPeriodIdx}º período`
               : ''
   
           // ── dados p/ "Evolução" ─────────────────────────────────────────────
           // Mesma comparação das barras, por matéria (muda só o formato: linhas).
           // Vale para os DOIS regimes — nota e conceito.
           const evolutionData = compareData.map((r) => ({
               axis: r.short,
               matter: r.matter,
               studentsAvg: r.studentsAvg,
               total: r.total,
           }))
   
           return {
               matters, periodCols, hasTotal, hasTotals, compareData, evolutionData,
               periodLabel, overallTrend, periodCount: periodIdxs.length, accLabel,
               isEmpty: matters.length === 0,
           }
       }, [students, periodTotals, isConcept, isTrimestral])
   
       const yFmt = (v) => (isConcept ? conceptFromNum(v) : v)
       const tipFmt = (v) => {
           if (v == null) return '—'
           return isConcept ? `${conceptFromNum(v)} (${Number(v).toFixed(1)})` : Number(v).toFixed(1)
       }
       const yDomain = isConcept ? [0, 4] : [0, 'auto']
       const yTicks = isConcept ? [0, 1, 2, 3, 4] : undefined
   
       // largura mínima do gráfico: ~78px por matéria (2 barras + espaçamento).
       // abaixo disso os rótulos se sobrepõem; acima, ativa scroll horizontal.
       const chartMinWidth = Math.max(0, (agg.matters?.length || 0) * 78)
   
       const commonAxis = { tickLine: false, tick: { fontSize: 11, fill: '#5b6b72' } }
       const tooltipStyle = {
           contentStyle: {
               borderRadius: 10, border: '1px solid var(--line)',
               boxShadow: '0 6px 18px rgba(0,0,0,.08)', fontSize: 12,
           },
           labelStyle: { color: 'var(--teal-text, var(--teal))', fontWeight: 700 },
       }
   
       return (
           <Wrapper>
               <Header onClick={() => setOpen(o => !o)} aria-expanded={open}>
                   <HeaderLeft>
                       <FiBarChart2 size={18} />
                       <div>
                           <Title>Desempenho da turma</Title>
                           <Subtitle>
                               {isConcept
                                   ? 'Conceito médio dos alunos × máximo (A)'
                                   : 'Nota média dos alunos × nota total'}
                           </Subtitle>
                       </div>
                   </HeaderLeft>
                   <Toggle $open={open} aria-hidden>
                       <FiChevronDown size={20} />
                   </Toggle>
               </Header>
   
               {open && (
                   <Body>
                       {loading ? (
                           <StateMsg>Carregando desempenho…</StateMsg>
                       ) : error ? (
                           <StateMsg>Não foi possível carregar o desempenho da turma.</StateMsg>
                       ) : agg.isEmpty ? (
                           <StateMsg>Ainda não há resultados lançados para este ano.</StateMsg>
                       ) : (
                           <>
                               <ViewSwitch role="tablist" aria-label="Modo de visualização">
                                   <SwitchGroup>
                                       <SwitchButton
                                           type="button" role="tab"
                                           aria-selected={view === 'compare'}
                                           $active={view === 'compare'}
                                           onClick={() => setView('compare')}
                                       >
                                           <FiBarChart2 size={14} /> Comparar
                                       </SwitchButton>
                                       <SwitchButton
                                           type="button" role="tab"
                                           aria-selected={view === 'evolution'}
                                           $active={view === 'evolution'}
                                           onClick={() => setView('evolution')}
                                       >
                                           <FiTrendingUp size={14} /> Evolução
                                       </SwitchButton>
                                   </SwitchGroup>
   
                                   {/* status geral da turma (1º → último período) */}
                                   {agg.overallTrend.considered > 0 ? (() => {
                                       const meta = TREND_META[agg.overallTrend.status]
                                       const { Icon } = meta
                                       const c = agg.overallTrend.counts
                                       return (
                                           <TrendBadge
                                               $color={meta.color}
                                               title={`${c.up} evoluindo · ${c.stable} estável · ${c.down} caindo`}
                                           >
                                               <Icon size={13} /> {meta.label}
                                           </TrendBadge>
                                       )
                                   })() : (
                                       // sem 2º período ainda → explica por que não há status
                                       <TrendHint title="A tendência aparece quando houver pelo menos dois períodos lançados.">
                                           {agg.periodCount <= 1
                                               ? 'Status disponível a partir do 2º período'
                                               : 'Tendência indisponível'}
                                       </TrendHint>
                                   )}
                               </ViewSwitch>
   
                               <ChartArea>
                                   <ChartScroll>
                                       <ChartInner $minWidth={chartMinWidth}>
                                           <ResponsiveContainer width="100%" height="100%">
                                       {view === 'evolution' ? (
                                           <LineChart
                                               data={agg.evolutionData}
                                               margin={{ top: 8, right: 12, bottom: 4, left: -8 }}
                                           >
                                               <CartesianGrid strokeDasharray="3 3" stroke="#eef2f4" vertical={false} />
                                               <XAxis dataKey="axis" interval={0} axisLine={{ stroke: 'var(--line)' }} {...commonAxis} />
                                               <YAxis
                                                   domain={yDomain} ticks={yTicks} tickFormatter={yFmt}
                                                   width={40} axisLine={{ stroke: 'var(--line)' }} {...commonAxis}
                                               />
                                               <Tooltip
                                                   formatter={(v, k) => [tipFmt(v), k]}
                                                   labelFormatter={(label, payload) => {
                                                       const full = payload && payload[0] && payload[0].payload
                                                       return (full && full.matter) ? full.matter : label
                                                   }}
                                                   {...tooltipStyle}
                                               />
                                               {/* média dos alunos × total, por matéria (nota e conceito) */}
                                               <Line
                                                   type="monotone"
                                                   dataKey="studentsAvg"
                                                   name="Média dos alunos"
                                                   stroke={STUDENTS_COLOR}
                                                   strokeWidth={2.5}
                                                   dot={{ r: 4, strokeWidth: 0, fill: STUDENTS_COLOR }}
                                                   activeDot={{ r: 6 }}
                                                   connectNulls
                                               />
                                               {agg.hasTotals && (
                                                   <Line
                                                       type="monotone"
                                                       dataKey="total"
                                                       name={isConcept ? 'Conceito máximo (A)' : 'Nota total'}
                                                       stroke={TOTAL_COLOR}
                                                       strokeWidth={2.5}
                                                       strokeDasharray="5 4"
                                                       dot={{ r: 4, strokeWidth: 0, fill: TOTAL_COLOR }}
                                                       activeDot={{ r: 6 }}
                                                       connectNulls
                                                   />
                                               )}
                                           </LineChart>
                                       ) : (
                                           <ComposedChart
                                               data={agg.compareData}
                                               margin={{ top: 8, right: 12, bottom: 4, left: -8 }}
                                           >
                                               <CartesianGrid strokeDasharray="3 3" stroke="#eef2f4" vertical={false} />
                                               <XAxis dataKey="short" interval={0} axisLine={{ stroke: 'var(--line)' }} {...commonAxis} />
                                               <YAxis
                                                   domain={yDomain} ticks={yTicks} tickFormatter={yFmt}
                                                   width={40} axisLine={{ stroke: 'var(--line)' }} {...commonAxis}
                                               />
                                               <Tooltip
                                                   formatter={(v, k) => [tipFmt(v), k]}
                                                   labelFormatter={(label, payload) => {
                                                       const full = payload && payload[0] && payload[0].payload
                                                       return (full && full.matter) ? full.matter : label
                                                   }}
                                                   {...tooltipStyle}
                                               />
   
                                               {/* média dos alunos × total, por matéria (nota e conceito) */}
                                               <Bar
                                                   dataKey="studentsAvg"
                                                   name="Média dos alunos"
                                                   fill={STUDENTS_COLOR}
                                                   radius={[4, 4, 0, 0]}
                                                   maxBarSize={26}
                                               />
                                               {agg.hasTotals && (
                                                   <Bar
                                                       dataKey="total"
                                                       name={isConcept ? 'Conceito máximo (A)' : 'Nota total'}
                                                       fill={TOTAL_COLOR}
                                                       radius={[4, 4, 0, 0]}
                                                       maxBarSize={26}
                                                   />
                                               )}
                                           </ComposedChart>
                                       )}
                                           </ResponsiveContainer>
                                       </ChartInner>
                                   </ChartScroll>
                               </ChartArea>
   
                               {/* ── legenda: média dos alunos × total (nota e conceito) ── */}
                               <Legend>
                                   <LegendItem title="Média do resultado dos alunos, por matéria">
                                       <Swatch style={{ background: STUDENTS_COLOR }} />
                                       Média dos alunos
                                   </LegendItem>
                                   {agg.hasTotals && (
                                       <LegendItem
                                           title={isConcept
                                               ? 'Conceito máximo possível (A)'
                                               : 'Nota total dos períodos lançados, por matéria'}
                                       >
                                           <Swatch style={{ background: TOTAL_COLOR }} />
                                           {isConcept ? 'Conceito máximo (A)' : 'Nota total'}
                                       </LegendItem>
                                   )}
                                   {!isConcept && agg.accLabel && (
                                       <LegendNote>({agg.accLabel})</LegendNote>
                                   )}
                               </Legend>
   
                               {/* selos de tendência por matéria (só na Evolução, quando há 2+ períodos) */}
                               {view === 'evolution' && agg.matters.some(m => m.trend && !m.trend.insufficient) && (
                                   <Legend>
                                       {agg.matters.map((m) => {
                                           const t = m.trend || {}
                                           if (t.insufficient) return null
                                           const meta = TREND_META[t.status] || TREND_META.stable
                                           const { Icon } = meta
                                           const sign = t.delta > 0 ? '+' : ''
                                           return (
                                               <MatterTrend
                                                   key={m.name}
                                                   $color={meta.color}
                                                   title={`${m.name} · do 1º ao último período: ${sign}${isConcept ? Number(t.delta).toFixed(1) : `${String(t.delta).replace('.', ',')} pontos percentuais de aproveitamento`}`}
                                               >
                                                   <Icon size={12} /> {m.short}
                                               </MatterTrend>
                                           )
                                       })}
                                   </Legend>
                               )}
                           </>
                       )}
                   </Body>
               )}
           </Wrapper>
       )
   }
   
   export default ClassPerformanceChart